import type React from "react";
import {
	type Dispatch,
	type SetStateAction,
	createRef,
	useCallback,
	useEffect,
	useState,
} from "react";
import type { ItemType } from "./data";

export type WordInputType = {
	guessWord: string;
	onHint: boolean;
	setAllSlotsFilled: Dispatch<SetStateAction<boolean>>;
	imageIsClear: boolean;
	word: string[];
	setWord: (word: string[]) => void;
	wordIsDone: boolean;
	currentImage: number;
	wordsData?: ItemType[];
	setWordsData: Dispatch<SetStateAction<ItemType[]>>;
	reduceMaxPoints: () => void;
};

const WordInput: React.FC<WordInputType> = ({
	guessWord,
	onHint,
	setAllSlotsFilled,
	imageIsClear,
	word,
	setWord,
	wordIsDone,
	currentImage,
	setWordsData,
	reduceMaxPoints,
}) => {
	const inputRefs = Array(guessWord.length)
		.fill(null)
		.map(() => createRef<HTMLInputElement>());

	const [inputClasses, setInputClasses] = useState(
		Array(guessWord.length).fill(""),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (imageIsClear) {
			setWord(guessWord.split(""));
		} else {
			setWord(Array(guessWord.length).fill(""));
		}
	}, [imageIsClear, guessWord]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (wordIsDone) {
			setWord(guessWord.split(""));
		}
		setInputClasses(Array(guessWord.length).fill(""));
	}, [currentImage]);

	const onChangeHandler = (
		e: React.ChangeEvent<HTMLInputElement>,
		index: number,
	) => {
		const newWord = [...word];
		newWord[index] = e.target.value;
		setWord(newWord);

		// if this is not the last input field, focus the next one
		if (index < word.length - 1 && e.target.value !== "") {
			inputRefs[index + 1]?.current?.focus();
		}
		// if this is the last input field and the value is not empty, update the word
		else if (index === word.length - 1 && e.target.value !== "") {
			setWord(newWord);
		}

		const allFilled = newWord.every((char) => char !== "");

		if (allFilled || wordIsDone) {
			setAllSlotsFilled(true);
			setWordsData((prevWordsData) =>
				prevWordsData.map((wordData, i) =>
					i === currentImage ? { ...wordData, allSlotsFilled: true } : wordData,
				),
			);
		} else {
			setAllSlotsFilled(false);
			setWordsData((prevWordsData) =>
				prevWordsData.map((wordData, i) =>
					i === currentImage
						? { ...wordData, allSlotsFilled: false }
						: wordData,
				),
			);
		}

		const newInputClasses = [...inputClasses];
		newInputClasses[index] = e.target.value !== "" ? "filled" : "";
		setInputClasses(newInputClasses);
	};

	const onKeyUpHandler = (
		e: React.KeyboardEvent<HTMLInputElement>,
		index: number,
	) => {
		if (e.key === "Backspace" && e.currentTarget.value === "") {
			if (index > 0) {
				inputRefs[index - 1]?.current?.focus();
			}
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const onHintHandler = useCallback(() => {
		const emptyIndices = word
			.map((letter, index) => (letter === "" ? index : -1))
			.filter((index) => index !== -1);
		if (emptyIndices.length !== 0) {
			const randomIndex =
				emptyIndices[Math.floor(Math.random() * emptyIndices.length)];
			const newWord = [...word];
			newWord[randomIndex] = guessWord[randomIndex];
			setWord(newWord);
			const newInputClasses = [...inputClasses];
			newInputClasses[randomIndex] = "filled";
			setInputClasses(newInputClasses);
			if (reduceMaxPoints) {
				reduceMaxPoints();
			}
		}

		if (imageIsClear) {
			setWord(guessWord.split(""));
		}
	}, [onHint, imageIsClear]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		onHintHandler();
	}, [onHint]);

	return (
		<div className="word-input">
			{word.map((letter, index) => {
				return (
					<input
						ref={inputRefs[index]}
						onChange={(e) => onChangeHandler(e, index)}
						onKeyUp={(e) => onKeyUpHandler(e, index)}
						key={index + letter}
						value={letter}
						maxLength={1}
						disabled={wordIsDone}
						className={inputClasses[index]}
					/>
				);
			})}
		</div>
	);
};

export default WordInput;
